// The governed select is a select-only combobox. Its native select remains the
// form owner; this hook supplies the reviewed popup and keyboard behavior.
//
// Placement and width belong to CSS anchor positioning (the `field-popup`
// utility), not to this hook. Measuring the trigger here is what used to clamp
// the popup to the trigger's width and clip long option labels.
const TYPEAHEAD_RESET_MS = 500

// Modified printable keys are shortcuts, not search input.
function isPrintable(event) {
  return (
    event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey
  )
}

export const SelectPopup = {
  mounted() {
    this.trigger = this.el.querySelector("[data-select-trigger]")
    this.native = this.el.querySelector("select")
    this.listbox = this.el.querySelector("[data-select-listbox]")
    this.cacheOptions()
    this.supportsPopover =
      typeof this.listbox.showPopover === "function" &&
      typeof this.listbox.hidePopover === "function"

    if (this.supportsPopover) {
      // Light dismiss closes the popover without going through close().
      this.onToggle = (event) => {
        if (event.newState === "open") {
          this.trigger.setAttribute("aria-expanded", "true")
          this.syncActive()
        } else {
          this.trigger.setAttribute("aria-expanded", "false")
          this.trigger.removeAttribute("aria-activedescendant")
        }
      }
      this.listbox.addEventListener("toggle", this.onToggle)
    } else {
      this.listbox.hidden = true
    }

    this.trigger.addEventListener("click", () => {
      if (!this.supportsPopover) this.toggle()
    })
    this.trigger.addEventListener("keydown", (event) => this.onKeydown(event))
    this.listbox.addEventListener("click", (event) => {
      const option = event.target.closest("[data-select-option]")
      if (!option || option.dataset.disabled === "true") return

      // An action commits no value — LiveView's document-level phx-click
      // handling and the GenericConfirmDialog interceptor still need to see
      // this click, so the popup closes without preventDefault/stopPropagation.
      if (option.hasAttribute("data-select-action")) {
        this.close()
        return
      }

      this.commit(option)
    })
    this.onDocumentPointerDown = (event) => {
      if (!this.el.contains(event.target)) this.close()
    }
    document.addEventListener("pointerdown", this.onDocumentPointerDown)
    this.syncActive()
  },

  updated() {
    // The option elements are server-owned: a commit removes the prompt from a
    // non-clearable select, and callers such as reminder recurrence swap the
    // whole set. Re-read them so the keyboard list can never commit a detached
    // node — doing so would write that node's value, clearing the selection.
    this.cacheOptions()
    this.syncActive()
  },

  destroyed() {
    clearTimeout(this.typeaheadTimer)
    document.removeEventListener("pointerdown", this.onDocumentPointerDown)
    if (this.onToggle) {
      this.listbox.removeEventListener("toggle", this.onToggle)
    }
  },

  cacheOptions() {
    this.options = Array.from(this.el.querySelectorAll("[data-select-option]"))
    this.activeIndex = this.selectedIndex()
  },

  open() {
    if (this.trigger.disabled) return
    if (this.supportsPopover) {
      if (!this.listbox.matches(":popover-open")) this.listbox.showPopover()
    } else {
      this.listbox.hidden = false
    }
    this.trigger.setAttribute("aria-expanded", "true")
    this.syncActive()
  },

  close() {
    if (this.supportsPopover) {
      if (this.listbox.matches(":popover-open")) this.listbox.hidePopover()
    } else {
      this.listbox.hidden = true
    }
    this.trigger.setAttribute("aria-expanded", "false")
    this.trigger.removeAttribute("aria-activedescendant")
  },

  toggle() {
    this.isOpen() ? this.close() : this.open()
  },

  isOpen() {
    return this.supportsPopover
      ? this.listbox.matches(":popover-open")
      : !this.listbox.hidden
  },

  onKeydown(event) {
    const open = this.isOpen()

    if (event.key === "Escape" && open) {
      event.preventDefault()
      this.close()
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const direction = event.key === "ArrowDown" ? 1 : -1
      this.open()

      // With nothing selected there is no position to move from, so the first
      // press lands on the end the arrow points at rather than one past it.
      if (this.activeIndex < 0) {
        this.land(direction === 1 ? 0 : this.options.length - 1, direction)
      } else {
        this.move(direction)
      }
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault()
      this.open()
      this.land(
        event.key === "Home" ? 0 : this.options.length - 1,
        event.key === "Home" ? 1 : -1,
      )
    } else if (event.key === " " && this.typeahead) {
      // Mid-typeahead a space is part of the search term ("New York"), not a
      // commit. Native selects behave the same way.
      event.preventDefault()
      this.typeaheadSeek(" ")
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      if (!open) this.open()
      else this.activate(this.options[this.activeIndex])
    } else if (event.key === "Tab") {
      this.close()
    } else if (isPrintable(event)) {
      // The native select this replaced gave typeahead for free; without it a
      // long option list — product types, timezones — can only be crossed one
      // arrow press at a time.
      event.preventDefault()
      this.open()
      this.typeaheadSeek(event.key)
    }
  },

  // A repeated single character cycles through the options starting with it,
  // which is how a native select disambiguates; anything else extends the
  // buffer. The buffer expires so an unrelated later keystroke starts fresh.
  typeaheadSeek(key) {
    clearTimeout(this.typeaheadTimer)
    this.typeaheadTimer = setTimeout(() => {
      this.typeahead = ""
    }, TYPEAHEAD_RESET_MS)

    const previous = this.typeahead || ""
    const repeated =
      previous.length > 0 && previous.split("").every((c) => c === key)
    this.typeahead = repeated ? key : previous + key

    const term = (repeated ? key : this.typeahead).toLowerCase()
    // An unselected list has no cursor to search from; start at the top.
    const base = Math.max(this.activeIndex, 0)
    const from = repeated ? base + 1 : base
    const length = this.options.length

    for (let offset = 0; offset < length; offset++) {
      const index = (from + offset + length) % length
      const option = this.options[index]

      if (
        option.dataset.disabled !== "true" &&
        (option.dataset.label || "").toLowerCase().startsWith(term)
      ) {
        this.activeIndex = index
        this.syncActive()
        return
      }
    }
  },

  // Puts the cursor on a specific index, then lets seek skip disabled options
  // in the given direction.
  land(index, direction) {
    this.activeIndex = index
    this.seek(direction)
    this.syncActive()
  },

  move(delta) {
    this.activeIndex += delta
    this.seek(delta)
    this.syncActive()
  },

  seek(direction) {
    const length = this.options.length

    for (let attempts = 0; attempts < length; attempts++) {
      this.activeIndex = (this.activeIndex + length) % length
      if (this.options[this.activeIndex].dataset.disabled !== "true") return
      this.activeIndex += direction
    }
  },

  // -1 means "no option is selected", which a prompted select never reports:
  // its prompt carries aria-selected while the field is empty. Collapsing that
  // into 0 would make an unprompted empty select look as though its first
  // option were current, and the first ArrowDown would then step past it.
  selectedIndex() {
    return this.options.findIndex(
      (option) => option.getAttribute("aria-selected") === "true",
    )
  },

  syncActive() {
    this.options.forEach((option, index) => {
      option.dataset.active = String(index === this.activeIndex)
    })

    const active = this.options[this.activeIndex]

    if (active && this.isOpen()) {
      this.trigger.setAttribute("aria-activedescendant", active.id)
      active.scrollIntoView({ block: "nearest" })
    }
  },

  // Enter/Space route through here so an action and a value-committing
  // option share the same activation path. An action never writes the
  // native value: closing first, then dispatching a genuine click, runs the
  // same phx-click + data-confirm-* path the pointer click takes.
  activate(option) {
    if (!option || option.dataset.disabled === "true") return

    if (option.hasAttribute("data-select-action")) {
      this.close()
      option.click()
      return
    }

    this.commit(option)
  },

  commit(option) {
    if (!option || option.dataset.disabled === "true") return
    // An action option commits no value; the keyboard path already routes
    // it through activate(), but this guard keeps commit() safe to call
    // directly and keeps the native value from ever being an action id.
    if (option.hasAttribute("data-select-action")) return

    this.activeIndex = this.options.indexOf(option)
    this.native.value = option.dataset.value
    this.native.dispatchEvent(new Event("input", { bubbles: true }))
    this.native.dispatchEvent(new Event("change", { bubbles: true }))
    this.el.querySelector("[data-select-value]").textContent =
      option.dataset.label
    this.options.forEach((candidate) => {
      candidate.setAttribute("aria-selected", String(candidate === option))
    })
    this.close()
  },
}
